// Mise en forme du contenu d'un message pour le modèle : texte de la question,
// documents glissés dans le chat, images. Fonctions pures, partagées par le
// circuit standard et l'agent (state/chat.ts) — et testées à part.
import type { AttachedDoc, Message } from "../db/types";
import type { ContentPart } from "./rcpLLM";

/** Texte de la question, documents glissés dans le chat inclus, entourés de
 *  délimiteurs explicites pour que le modèle les distingue de la question. */
export function composeText(userText: string, docs?: AttachedDoc[]): string {
  let textBlock = userText;
  for (const d of docs ?? []) {
    textBlock += `\n\n[Document joint : ${d.name}]\n${d.text}\n[Fin du document : ${d.name}]`;
  }
  return textBlock;
}

/** Contenu multimodal : une chaîne simple quand il n'y a pas d'image (le cas
 *  courant), sinon un tableau de parties texte + images. */
export function composeContent(
  userText: string,
  docs?: AttachedDoc[],
  images?: string[],
): string | ContentPart[] {
  const textBlock = composeText(userText, docs);
  if (images && images.length > 0) {
    return [
      { type: "text", text: textBlock },
      ...images.map<ContentPart>((url) => ({ type: "image_url", image_url: { url } })),
    ];
  }
  return textBlock;
}

/** Contenu d'un message enregistré, pièces jointes rejouées : appliqué à
 *  l'historique pour que les documents et images restent en contexte toute la
 *  conversation. Les réponses de l'assistant sont toujours du texte seul. */
export function toLLMContent(m: Message): string | ContentPart[] {
  if (m.role === "assistant") return m.content;
  return composeContent(m.content, m.attachedDocs, m.images);
}

/** Historique envoyé à l'agent : du texte seul. Les images des tours passés
 *  ne sont pas rejouées (seulement signalées) — celles de la question en cours
 *  partent à part. */
export function toAgentText(m: Message): string {
  if (m.role === "assistant") return m.content;
  const n = m.images?.length ?? 0;
  const note = n > 0 ? `\n[${n} image${n > 1 ? "s" : ""} jointe${n > 1 ? "s" : ""}]` : "";
  return composeText(m.content, m.attachedDocs) + note;
}
